// MarketDepthPanel — lifted verbatim out of LiveTrading.tsx (God 2026-08-26).
// Same code, smaller address: a UI problem in this panel is now a single file to
// read instead of a 6,900-line one. Imports are derived from what the block
// actually references, not guessed.
//
// ── God 2026-08-26, DEPTH pass ────────────────────────────────────────────────
// Two things changed, and they are different in kind.
//
// 1. THEME. The panel was hardcoded sky/cyan, so on the KALI page — the
//    real-money lane — it looked exactly like the paper lane. Only the CHROME
//    (shell, header, chips, SUB, glow) follows the lane now. The ladder's green
//    and red do NOT: there, green means BID and red means ASK. That is data
//    semantics, not decoration, and repainting it KALI-red would destroy the one
//    distinction the whole panel exists to draw.
//
// 2. HONESTY. The title said "20 LEVEL", the footer said "20-depth book", and
//    the body rendered a hardcoded 20 rows — over a book that is frequently 5
//    levels deep. Dhan enables Full (20-level) Depth for NSE equity/derivatives
//    ONLY; every MCX book is a 5-level REST quote. mcx_depth_poller.py says so
//    outright ("it is not twenty, and this file never pretends otherwise"), but
//    the WS payload dropped depth_levels and source before they reached the
//    browser, so the panel could not have known. It now sizes itself to the book
//    it was actually given and names its own source. 15 blank rows under a
//    "20 LEVEL" banner read as a dead feed; that was the bug.
//
// The server half of (2) is in modules/dhan_feed.py::_read_payload.

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { recordDepthSnap } from '../services/botMemory';
import { BarChart2, Search, X } from 'lucide-react';
import { API_BASE_URL, DEPTH_WS_URL, NIFTY_50_FNO_LIST, INDICES_LIST, COMMODITIES_LIST, ETFS, _normSym, useActiveSymbols, MarketDepthData } from './foundation';
import { mcxCloseLabel } from '../lib/marketHours';

// ── Lane chrome ───────────────────────────────────────────────────────────────
// KALI is the real-money lane and reads RED (matching KaliSecurityPanel and the
// LiveTrading header); DUMABATI/paper reads sky. Semantic colours — bid green,
// ask red, LTP amber — are deliberately NOT in here: they mean the same thing in
// both lanes and must never move.
type DepthChrome = {
  accent: string; accentDim: string; accentSoft: string; accentFaint: string;
  shell: string; glow: string; border: string;
  headerBg: string; subBg: string; panelBg: string; footBg: string;
  hairline: string; label: string; muted: string; faint: string;
};
const CHROME: Record<'kali' | 'paper', DepthChrome> = {
  paper: {
    accent: '#38bdf8', accentDim: '#7dd3fc', accentSoft: 'rgba(56,189,248,.20)', accentFaint: 'rgba(56,189,248,.07)',
    shell: 'linear-gradient(180deg,#060b18,#0a0f1e)', glow: '0 20px 64px rgba(56,189,248,.25)', border: '#38bdf8',
    headerBg: 'linear-gradient(90deg,rgba(56,189,248,.12),rgba(56,189,248,.04))',
    subBg: '#040810', panelBg: '#06080f', footBg: '#070b13',
    hairline: '#1a2138', label: '#94a3b8', muted: '#64748b', faint: '#475569',
  },
  kali: {
    accent: '#ef4444', accentDim: '#fca5a5', accentSoft: 'rgba(239,68,68,.20)', accentFaint: 'rgba(239,68,68,.07)',
    shell: 'linear-gradient(180deg,#120608,#1a0a0d)', glow: '0 20px 64px rgba(239,68,68,.28)', border: '#ef4444',
    headerBg: 'linear-gradient(90deg,rgba(239,68,68,.14),rgba(239,68,68,.04))',
    subBg: '#0a0406', panelBg: '#0d0508', footBg: '#0f0609',
    hairline: '#2a1216', label: '#cbb2b5', muted: '#8b6f73', faint: '#6b5054',
  },
};


/** HH:MM:SS (IST) of a depth frame's timestamp - number (s/ms), ISO string or a bare clock string. */
const snapClock = (ts: any): string => {
  if (typeof ts === 'number' && isFinite(ts)) {
    return new Date(ts > 1e12 ? ts : ts * 1000).toLocaleTimeString('en-IN', { hour12: false, timeZone: 'Asia/Kolkata' });
  }
  const str = String(ts ?? '');
  if (/^\d{4}-\d{2}-\d{2}T/.test(str)) {
    const d = new Date(str);
    if (!isNaN(d.getTime())) return d.toLocaleTimeString('en-IN', { hour12: false, timeZone: 'Asia/Kolkata' });
  }
  const m = str.match(/\d{1,2}:\d{2}:\d{2}/);
  return m ? m[0] : (str || '—');
};

export const MarketDepthPanel: React.FC<{
  symbol: string;
  open: boolean;
  onClose: () => void;
  onSymbolChange: (s: string) => void;
  focus?: { underlying?: string; side?: 'CE' | 'PE'; strike?: number } | null;   // bot → picker sync
  kali?: boolean;                                                                // real-money lane → red chrome
}> = ({ symbol, open, onClose, onSymbolChange, focus, kali = false }) => {
  const C = CHROME[kali ? 'kali' : 'paper'];
  const actSyms = useActiveSymbols(open);   // PASS-hui symbols hi chips me (God 2026-07-14)
  const [depth, setDepth] = useState<MarketDepthData | null>(null);
  const [connected, setConnected] = useState(false);
  const [wsError, setWsError] = useState('');
  const [waitNote, setWaitNote] = useState('');   // server's WHY-no-data note
  const [marketClosed, setMarketClosed] = useState(false);
  // Mirrors marketClosed for the WS callback, which closes over a stale state
  // value: the handler is created once per connection, so reading the state
  // directly there would test whatever it was when the socket opened.
  const marketClosedRef = useRef(false);
  const [inputSym, setInputSym] = useState(symbol);
  const [selCat, setSelCat] = useState<'ALL' | 'INDEX' | 'STOCK' | 'ETF' | 'COMMODITY'>('ALL');
  const [symSearch, setSymSearch] = useState('');
  const wsRef = useRef<WebSocket | null>(null);
  const reconnTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const depthSnapTsRef = useRef(0);   // 🧠 bot-memory snapshot throttle (5s)

  // ── SMART OPTION PICKER — same chain service as Smart Option Pricer ───────
  // Indices + MCX commodities + top-liquid F&O stocks (all have option chains).
  // GOD 2026-07-14: sirf PASSED (active) underlyings — depth ka option picker
  const OPT_UNDERLYINGS = [
    'NIFTY_50', 'BANK_NIFTY', 'FIN_NIFTY', 'SENSEX',
    'GOLD', 'SILVER', 'CRUDEOIL', 'NATURALGAS',
    'RELIANCE', 'ICICIBANK', 'MARUTI', 'SBIN', 'WIPRO',
    'BHARTIARTL', 'HINDALCO', 'DIVISLAB', 'HEROMOTOCO', 'BAJAJ_AUTO',
  ];
  const [pkUnd, setPkUnd] = useState('NIFTY_50');
  const [pkExpiries, setPkExpiries] = useState<string[]>([]);
  const [pkExp, setPkExp] = useState('');
  const [pkSide, setPkSide] = useState<'CE' | 'PE'>('CE');
  const [pkChain, setPkChain] = useState<any[]>([]);
  const [pkSpot, setPkSpot] = useState(0);
  const [pkStrike, setPkStrike] = useState<number | null>(null);
  const [pkLoading, setPkLoading] = useState(false);
  const [pkMsg, setPkMsg] = useState('');
  const [pkFollow, setPkFollow] = useState(false);   // 🔗 BOTS: auto-follow active signal
  const pkDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const pkPending = useRef<{ strike: number; side: 'CE' | 'PE' } | null>(null);
  const pkArmed = useRef(true);
  const pkLastSub = useRef('');

  useEffect(() => {   // underlying → expiries (shared service; Dhan OC = 1 req/3s → retry)
    if (!open) return;
    let dead = false, tries = 0;
    setPkExpiries([]); setPkExp(''); setPkChain([]); setPkStrike(null); setPkMsg('');
    const load = () => {
      fetch(`${API_BASE_URL}/options/expiry?symbol=${pkUnd}`)
        .then(r => r.json())
        .then(d => {
          if (dead) return;
          const ex = d?.expiries || [];
          if (ex.length) { setPkExpiries(ex); setPkExp(ex[0]); setPkMsg(''); }
          else if (++tries <= 5) { setPkMsg(`expiry busy (rate-limit) — retry ${tries}/5…`); setTimeout(load, 3200); }
          else setPkMsg('expiry list failed — re-select underlying');
        })
        .catch(() => {
          if (dead) return;
          if (++tries <= 5) { setPkMsg(`expiry retry ${tries}/5…`); setTimeout(load, 3200); }
          else setPkMsg('expiry list failed');
        });
    };
    load();
    return () => { dead = true; };
  }, [pkUnd, open]);

  useEffect(() => {   // underlying + expiry → chain (strikes + LIVE LTPs + spot/ATM; retry on rate-limit)
    if (!open || !pkExp) return;
    let dead = false, tries = 0;
    setPkLoading(true); setPkMsg('');
    const load = () => {
      fetch(`${API_BASE_URL}/options/chain?symbol=${pkUnd}&expiry=${pkExp}`)
        .then(r => r.json())
        .then(d => {
          if (dead) return;
          // normalize: endpoint rows are {strikePrice, is_atm, CE:{ltp}, PE:{ltp}}
          const rows = (d?.data || d?.chain || []).map((r: any) => ({
            strike: Number(r?.strikePrice ?? r?.strike ?? 0),
            ce_ltp: Number(r?.CE?.ltp ?? r?.ce_ltp ?? 0),
            pe_ltp: Number(r?.PE?.ltp ?? r?.pe_ltp ?? 0),
            is_atm: !!r?.is_atm,
          })).filter((r: any) => r.strike > 0);
          if (rows.length) {
            setPkChain(rows);
            setPkSpot(Number(d?.spot) || 0);
            setPkLoading(false);
            const p = pkPending.current;   // ⚡ SIGNAL OPTION queued before chain was ready
            if (p && rows.some((r: any) => Number(r.strike) === p.strike)) {
              setPkSide(p.side); setPkStrike(p.strike); pkPending.current = null;
            }
          } else if (++tries <= 5) { setPkMsg(`chain busy (rate-limit) — retry ${tries}/5…`); setTimeout(load, 3200); }
          else { setPkLoading(false); setPkMsg('chain empty — try another expiry'); }
        })
        .catch(() => {
          if (dead) return;
          if (++tries <= 5) { setPkMsg(`chain retry ${tries}/5…`); setTimeout(load, 3200); }
          else { setPkLoading(false); setPkMsg('chain load failed'); }
        });
    };
    load();
    return () => { dead = true; };
  }, [pkUnd, pkExp, open]);

  // AUTO-SUB (debounced ~400ms): pick → resolve token → swap 20-level depth sub.
  //
  // ARMED/DISARMED (God 2026-09-07). This effect used to fire on chain or strike
  // changes NO MATTER what the user had actually selected. Pick COMMODITY →
  // CRUDEOILM and the picker was still sitting on NIFTY 23800 CE: the chain
  // reloaded, this fired, and the panel printed "strike 23800 CE resolve failed
  // — not in chain" over the commodity the user had just chosen. Worse, on a
  // SUCCESSFUL resolve it called onSymbolChange(sid) and silently replaced the
  // commodity with a NIFTY option. A chip click disarms it; touching any picker
  // control arms it again. Default armed, so opening the modal still lands on
  // the ATM book exactly as before.
  useEffect(() => {
    if (!open || !pkArmed.current || pkStrike == null || !pkChain.length) return;
    if (!pkChain.some(r => Number(r.strike) === Number(pkStrike))) {
      setPkMsg(`strike ${pkStrike} chain me nahi — invalid`); return;   // impossible strike → no SUB
    }
    const subKey = `${pkUnd}|${pkExp}|${pkStrike}|${pkSide}`;
    if (pkLastSub.current === subKey) return;   // already subbed this exact pick
    if (pkDebounce.current) clearTimeout(pkDebounce.current);
    pkDebounce.current = setTimeout(() => {
      setPkMsg('subscribing depth…');
      fetch(`${API_BASE_URL}/api/depth20/manual`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ symbol: pkUnd, strike: pkStrike, opt_type: pkSide, expiry: pkExp }),
      })
        .then(r => r.json())
        .then(d => {
          if (d?.status === 'success' && d.security_id) {
            pkLastSub.current = subKey;
            const friendly = `${pkUnd.replace(/_/g, ' ')} ${pkStrike} ${pkSide}`;
            setInputSym(friendly);
            onSymbolChange(String(d.security_id));   // numeric sid → depth20:{sid} stream
            setPkMsg(`✓ 20L depth LIVE: ${friendly} (LTP ₹${d.ltp ?? '—'})`);
          } else setPkMsg(d?.message || 'subscribe failed');
        })
        .catch(() => setPkMsg('subscribe failed'));
    }, 400);
    return () => { if (pkDebounce.current) clearTimeout(pkDebounce.current); };
  }, [pkStrike, pkSide, pkExp, pkChain]);   // pkChain dep: strike queued before chain loads still subs

  // Jump the picker to a signal-option payload (shared by the ⚡ button + 🔗 BOTS follow).
  const lastFollowSym = useRef('');
  const jumpToSignal = (d: any, viaFollow = false) => {
    // Deliberately choosing an option: hand the subscription back to the picker.
    pkArmed.current = true;
    if (!(d?.status === 'success' && d.strike)) {
      if (!viaFollow) setPkMsg('no active signal option');
      return;
    }
    if (viaFollow && d.option_symbol === lastFollowSym.current) return;   // already on it
    lastFollowSym.current = d.option_symbol;
    const side: 'CE' | 'PE' = d.opt_type === 'PE' ? 'PE' : 'CE';
    const tok = String(d.option_symbol || '').split(' ')[0];
    const ALIAS: Record<string, string> = { NIFTY: 'NIFTY_50', BANKNIFTY: 'BANK_NIFTY', FINNIFTY: 'FIN_NIFTY' };
    const und = OPT_UNDERLYINGS.includes(tok) ? tok : (ALIAS[tok] || pkUnd);
    setPkMsg(`${viaFollow ? '🔗 following' : 'signal'}: ${d.option_symbol}`);
    if (und !== pkUnd) {
      pkPending.current = { strike: Number(d.strike), side };   // applied when new chain arrives
      setPkUnd(und);                                            // switch underlying to the signal's
    } else {
      pkPending.current = null;
      setPkSide(side);
      setPkStrike(Number(d.strike));            // triggers auto-sub
    }
  };

  const useSignalOption = () => {
    setPkMsg('loading signal option…');
    fetch(`${API_BASE_URL}/api/signal/active-option`)
      .then(r => r.json())
      .then(d => jumpToSignal(d, false))
      .catch(() => setPkMsg('signal lookup failed'));
  };

  // BOT → PICKER SYNC: opening DEPTH for a bot aligns the picker to THAT bot's
  // underlying + side + strike (so strike & symbol stay connected, not stuck on NIFTY).
  const lastFocus = useRef('');
  useEffect(() => {
    if (!open || !focus) return;
    const fk = `${focus.underlying || ''}|${focus.side || ''}|${focus.strike || ''}`;
    if (lastFocus.current === fk) return;
    lastFocus.current = fk;
    const ALIAS: Record<string, string> = {
      NIFTY: 'NIFTY_50', NIFTY50: 'NIFTY_50', 'NIFTY 50': 'NIFTY_50',
      BANKNIFTY: 'BANK_NIFTY', 'BANK NIFTY': 'BANK_NIFTY', NIFTYBANK: 'BANK_NIFTY',
      FINNIFTY: 'FIN_NIFTY', 'FIN NIFTY': 'FIN_NIFTY',
      MIDCPNIFTY: 'MIDCPNIFTY', SENSEX: 'SENSEX', BANKEX: 'BANKEX',
    };
    const raw = String(focus.underlying || '').toUpperCase().trim();
    const und = OPT_UNDERLYINGS.includes(raw) ? raw : (ALIAS[raw] || ALIAS[raw.replace(/\s+/g, '')] || '');
    if (!und) { setPkMsg('ℹ️ stock/ETF depth — index option picker N/A for this bot'); return; }
    if (focus.side) setPkSide(focus.side);
    if (focus.strike) {
      // strike applied once the chain for this underlying loads (pkPending hook)
      pkPending.current = { strike: focus.strike, side: focus.side || pkSide };
    }
    setPkUnd(und);   // triggers expiry+chain reload → pkPending applies the strike
  }, [focus, open]);

  // 🔗 BOTS follow mode — panel auto-jumps to the newest open bot position's strike.
  useEffect(() => {
    if (!open || !pkFollow) return;
    let dead = false;
    const tick = () => {
      fetch(`${API_BASE_URL}/api/signal/active-option`)
        .then(r => r.json())
        .then(d => { if (!dead) { if (d?.status === 'success') jumpToSignal(d, true); else setPkMsg('🔗 following bots — no open position yet'); } })
        .catch(() => {});
    };
    tick();
    // 🔁 hidden-pause — follow-mode jump display-only hai
    const id = setInterval(() => { if (!document.hidden) tick(); }, 4000);
    return () => { dead = true; clearInterval(id); };
  }, [open, pkFollow, pkUnd]);

  // LIVE order-book bias from the streaming book (the data bots + Mamta AI now use).
  const bookBias = (() => {
    const bq = (depth?.bids || []).reduce((s, b) => s + (b.qty || 0), 0);
    const aq = (depth?.asks || []).reduce((s, a) => s + (a.qty || 0), 0);
    if (bq + aq <= 0) return null;
    const buyPct = Math.round(bq * 1000 / (bq + aq)) / 10;
    const label = buyPct >= 65 ? 'STRONG BUY' : buyPct >= 56 ? 'BUY'
      : buyPct <= 35 ? 'STRONG SELL' : buyPct <= 44 ? 'SELL' : 'NEUTRAL';
    return { buyPct, label };
  })();

  const pkAtmIdx = (() => {
    if (!pkChain.length) return -1;
    const flagged = pkChain.findIndex((r: any) => r.is_atm);   // server-marked ATM wins
    if (flagged >= 0) return flagged;
    if (!pkSpot) return -1;
    let bi = 0, bd = Infinity;
    pkChain.forEach((r, i) => { const d = Math.abs(Number(r.strike) - pkSpot); if (d < bd) { bd = d; bi = i; } });
    return bi;
  })();

  const symRef = useRef(symbol);   // current wanted symbol — onmessage filter + reconnect both read this
  const depthAliveRef = useRef(false);   // gates reconnect: false after unmount/close so a late onclose never re-opens
  const connect = useCallback(() => {
    if (!open) return;
    // Kill any lingering socket FIRST — a stale onclose must never reconnect the old symbol
    const prev = wsRef.current;
    if (prev) { prev.onopen = null; prev.onmessage = null; prev.onerror = null; prev.onclose = null; try { prev.close(); } catch {} }
    try {
      const ws = new WebSocket(DEPTH_WS_URL);
      wsRef.current = ws;
      ws.onopen = () => {
        setConnected(true); setWsError('');
        ws.send(JSON.stringify({ type: 'subscribe', symbol: symRef.current }));
      };
      ws.onmessage = (e) => {
        try {
          const j = JSON.parse(e.data as string);
          if (j.market_closed) {
            marketClosedRef.current = true;
            setMarketClosed(true);
            // Keep the last book on screen (labelled as a snapshot in the
            // closed banner) instead of blanking the panel; a symbol switch
            // still clears it, so it is never another instrument's book.
            return;
          }
          // LOCK LIFTED -> RE-SUBSCRIBE (God 2026-09-07). While the server
          // believed the market was shut it sent no book, so the socket has been
          // idle on this symbol; asking again costs one small frame and removes
          // any chance of sitting on a stale subscription once the gate opens.
          if (marketClosedRef.current) {
            marketClosedRef.current = false;
            try {
              wsRef.current?.send(JSON.stringify({ type: 'subscribe', symbol: symRef.current }));
            } catch { /* socket already gone — the reconnect path will resubscribe */ }
          }
          setMarketClosed(false);
          if (j.type === 'status' && j.note) { setWaitNote(String(j.note)); return; }   // server explains WHY no data
          // Drop frames from an older subscription — server echoes the subscribed string
          if (j.symbol && String(j.symbol) !== String(symRef.current)) return;
          // Accept both raw depth payload and wrapped { type:'depth', ... }
          if (j.bids || j.asks || j.type === 'depth') {
            setWaitNote('');
            const _bids = (j.bids || []).slice(0, 20);
            const _asks = (j.asks || []).slice(0, 20);
            setDepth({
              symbol: j.symbol || symRef.current,
              bids: _bids,
              asks: _asks,
              ltp: j.ltp, spread: j.spread,
              // Trust the server's own count when it sends one; otherwise fall
              // back to what actually arrived. Never assume 20.
              depthLevels: Number(j.depthLevels) || Math.max(_bids.length, _asks.length) || 0,
              source: typeof j.source === 'string' ? j.source : '',
              crossed: !!j.crossed,
              timestamp: j.timestamp || new Date().toLocaleTimeString('en-IN', { hour12: false }),
            });
            // 🧠 Feed the bots' short memory — latest 20-level book snapshot,
            // throttled to 5s (WS fires multiple frames/sec; localStorage write
            // per frame would thrash). Self-analysis reads the imbalance.
            const nowTs = Date.now();
            if (nowTs - depthSnapTsRef.current > 5000) {
              depthSnapTsRef.current = nowTs;
              recordDepthSnap(String(j.symbol || symRef.current),
                (j.bids || []).slice(0, 20), (j.asks || []).slice(0, 20), j.ltp, j.spread);
            }
          }
        } catch {}
      };
      ws.onerror = () => setWsError('Connection error — retrying in 3s…');
      ws.onclose = () => {
        setConnected(false);
        // depthAliveRef guards the reconnect: after unmount / panel-close a late
        // async onclose must NOT schedule a fresh socket (was a background
        // reconnect loop + setState-after-unmount).
        if (open && depthAliveRef.current) reconnTimer.current = setTimeout(connect, 3000);
      };
    } catch (err: any) { setWsError(err.message); }
  }, [open]);   // NOT symbol — symbol swaps re-subscribe on the SAME socket (no reconnect churn)

  useEffect(() => {
    const killSocket = () => {
      const w = wsRef.current;
      // null the handlers BEFORE close so the async onclose can't fire a reconnect
      if (w) { w.onopen = null; w.onmessage = null; w.onerror = null; w.onclose = null; try { w.close(); } catch {} }
    };
    if (!open) {
      depthAliveRef.current = false;
      killSocket();
      if (reconnTimer.current) clearTimeout(reconnTimer.current);
      setDepth(null); setConnected(false); setWsError('');
      return;
    }
    depthAliveRef.current = true;
    // 🔁 Mobile background/foreground + network-flap self-heal — a phone browser
    // silently kills a backgrounded socket; on return the readyState is dead but
    // no close event fired, so the depth book froze. Mirror the signals-WS pattern.
    const ensureAlive = () => {
      if (!depthAliveRef.current) return;
      const w = wsRef.current;
      if (!w || w.readyState === WebSocket.CLOSED || w.readyState === WebSocket.CLOSING) connect();
    };
    const onVis = () => { if (!document.hidden) ensureAlive(); };
    window.addEventListener('online', ensureAlive);
    document.addEventListener('visibilitychange', onVis);
    connect();
    return () => {
      depthAliveRef.current = false;   // stop reconnect-after-unmount (the H32 leak)
      window.removeEventListener('online', ensureAlive);
      document.removeEventListener('visibilitychange', onVis);
      killSocket();
      if (reconnTimer.current) clearTimeout(reconnTimer.current);
    };
  }, [open, connect]);

  // Re-subscribe when parent pushes a new symbol
  useEffect(() => {
    symRef.current = symbol;   // filter + (re)connect read this
    if (!/^\d+$/.test(symbol)) setInputSym(symbol);   // numeric sid (picker sub) → keep friendly name in the box
    setMarketClosed(false);   // clear stale closed banner immediately on symbol switch
    setDepth(null);           // never show the previous instrument's book
    if (connected && wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'subscribe', symbol }));
    }
  }, [symbol, connected]);

  if (!open) return null;

  const maxBidQty = Math.max(...(depth?.bids || []).map(b => b.qty), 1);
  const maxAskQty = Math.max(...(depth?.asks || []).map(a => a.qty), 1);
  const totalBidQty = depth?.bids.reduce((s, b) => s + b.qty, 0) ?? 0;
  const totalAskQty = depth?.asks.reduce((s, a) => s + a.qty, 0) ?? 0;
  const spread = depth?.spread ??
    (depth?.asks[0] && depth?.bids[0] ? depth.asks[0].price - depth.bids[0].price : 0);
  // Pro-DOM redesign (God 2026-07-27): cumulative depth (walls saaf dikhein) +
  // 2.5x-avg wall detection — bots ka depth_vision bhi yahi walls dekhta hai.
  const cumBids: number[] = []; let _cb = 0; (depth?.bids || []).forEach(b => { _cb += b.qty; cumBids.push(_cb); });
  const cumAsks: number[] = []; let _ca = 0; (depth?.asks || []).forEach(a => { _ca += a.qty; cumAsks.push(_ca); });
  const avgBidQty = totalBidQty / Math.max((depth?.bids || []).length, 1);
  const avgAskQty = totalAskQty / Math.max((depth?.asks || []).length, 1);

  const colHeader: React.CSSProperties = {
    fontSize: 8, color: C.muted, fontWeight: 700, letterSpacing: 1,
    textTransform: 'uppercase', padding: '6px 8px',
  };

  // ── What this book ACTUALLY is ───────────────────────────────────────────────
  // rows: never a hardcoded 20 again. A 5-level MCX book draws 5 rows, so there
  // are no blank rows left to mistake for a dead feed.
  const bookLevels = Math.max(depth?.depthLevels || 0, (depth?.bids || []).length, (depth?.asks || []).length);
  const rowCount = Math.min(Math.max(bookLevels, 1), 20);
  const src = depth?.source || '';
  const isFullDepth = src === 'twentydepth' || bookLevels > 5;
  // MCX is not a degraded NSE book — it is the deepest book the exchange offers
  // there. The wording matters: this is a ceiling, not a fault to go chase.
  const srcLabel = !depth ? ''
    : isFullDepth ? 'Dhan Full Depth · twentydepth WS'
    : src ? 'Dhan REST quote · exchange 5-level ceiling'
    : 'source unreported';

  return (
    <div style={{
      position: 'fixed', top: 60, left: 20, width: 'min(700px, 95vw)',
      maxHeight: '92vh',
      background: C.shell,
      borderRadius: 14, border: `1px solid ${C.border}`,
      boxShadow: C.glow,
      display: 'flex', flexDirection: 'column', zIndex: 9997, overflow: 'hidden',
    }}>
      {/* Header — the depth claim is now derived from the book, not asserted */}
      <div style={{ padding: '12px 16px', background: C.headerBg, borderBottom: `1px solid ${C.hairline}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 900, color: C.accent, letterSpacing: 1, display: 'flex', alignItems: 'center', gap: 7 }}>
            <BarChart2 size={13} /> MARKET DEPTH{depth ? ` — ${bookLevels} LEVEL` : ''}
            {kali && (
              <span style={{ fontSize: 8, fontWeight: 900, padding: '1px 6px', borderRadius: 4, letterSpacing: .8,
                background: 'rgba(239,68,68,.18)', border: '1px solid rgba(239,68,68,.5)', color: '#fca5a5' }}>
                🔱 LIVE AUTO TRADING KALI
              </span>
            )}
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: connected ? '#10b981' : '#ef4444', display: 'inline-block', animation: connected ? 'pulse 2s infinite' : 'none' }} />
          </div>
          <div style={{ fontSize: 9, color: C.label, marginTop: 2 }}>
            {depth ? `${srcLabel} · Orders · Qty · Price` : 'Order book · Orders · Qty · Price'}
          </div>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.label, cursor: 'pointer' }}><X size={16} /></button>
      </div>

      {/* ── Symbol selector — smart search + category pills + filtered grid ── */}
      <div style={{ padding: '8px 14px', background: C.subBg, borderBottom: `1px solid ${C.hairline}` }}>
        {/* ⚡ ALWAYS-LIVE BOOKS (God 2026-07-27 redesign): depth20 producer ke
            always-on FUT L2 streams — 1 tap = turant zinda book, koi wait nahi */}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 7, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 8.5, color: C.accent, fontWeight: 800, letterSpacing: 1 }}>⚡ ALWAYS-LIVE</span>
          {([['NIFTY FUT', 'NIFTY_FUT_NEAR_L2'], ['BANKNIFTY FUT', 'BANKNIFTY_FUT_NEAR_L2']] as const).map(([label, sub]) => (
            <button key={sub} onClick={() => { setInputSym(label); onSymbolChange(sub); }}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 99, fontSize: 10, fontWeight: 900, cursor: 'pointer',
                background: symbol === sub ? C.accentSoft : C.accentFaint,
                border: `1.5px solid ${symbol === sub ? C.accent : C.accentFaint}`, color: C.accent }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981', boxShadow: '0 0 5px rgba(16,185,129,.9)' }} />
              {label}
            </button>
          ))}
          <span style={{ fontSize: 8, color: C.faint }}>· FUT L2 book 24×7 stream me hai — instant data</span>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 7, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: '1 1 200px', minWidth: 150 }}>
            <Search size={12} color="#475569" style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)' }} />
            <input
              value={symSearch}
              onChange={e => setSymSearch(e.target.value)}
              placeholder="Search symbol… (NIFTY, RELIANCE, GOLD)"
              style={{ width: '100%', padding: '6px 10px 6px 28px', borderRadius: 7, fontSize: 11, fontWeight: 600,
                background: 'rgba(255,255,255,.04)', border: `1px solid ${C.hairline}`, color: '#e2e8f0', outline: 'none' }}
            />
            {symSearch && (
              <button onClick={() => setSymSearch('')} style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', color: C.muted, cursor: 'pointer', padding: 0, lineHeight: 0 }}><X size={12} /></button>
            )}
          </div>
          {(['ALL', 'INDEX', 'STOCK', 'ETF', 'COMMODITY'] as const).map(c => (
            <button key={c} onClick={() => setSelCat(c)}
              style={{ padding: '5px 10px', borderRadius: 6, fontSize: 9, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap',
                background: selCat === c ? C.accentSoft : 'rgba(255,255,255,.03)',
                border: `1px solid ${selCat === c ? C.accent : C.hairline}`,
                color: selCat === c ? C.accentDim : C.muted }}>{c}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', maxHeight: 88, overflowY: 'auto' }}>
          {(() => {
            const _cat: Record<string, string[]> = {
              INDEX: INDICES_LIST, STOCK: NIFTY_50_FNO_LIST, ETF: ETFS, COMMODITY: COMMODITIES_LIST,
            };
            const q = symSearch.trim().toUpperCase();
            const list = selCat === 'ALL'
              ? [...INDICES_LIST, ...COMMODITIES_LIST, ...NIFTY_50_FNO_LIST, ...ETFS]
              : (_cat[selCat] || []);
            // sirf PASS-hui (active) symbols — fail-open jab tak list load na ho
            const matches = list.filter(s => (!q || s.includes(q)) && (!actSyms || actSyms.has(_normSym(s))));
            if (!matches.length) return <span style={{ fontSize: 10, color: C.faint, padding: '6px 2px' }}>No symbol matches “{symSearch}”</span>;
            return matches.map(s => (
              <button key={s} onClick={() => {
                  // Disarm the option picker: this chip is now what the panel is
                  // showing, and the picker must not subscribe over it.
                  pkArmed.current = false;
                  pkLastSub.current = '';
                  setPkMsg('');
                  setInputSym(s); onSymbolChange(s);
                }}
                style={{ padding: '5px 12px', borderRadius: 6, fontSize: 10, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all .15s',
                  background: symbol === s ? C.accentSoft : 'rgba(255,255,255,.04)',
                  border: `1px solid ${symbol === s ? C.accent : C.hairline}`,
                  color: symbol === s ? C.accentDim : C.label }}>{s}</button>
            ));
          })()}
        </div>
        {INDICES_LIST.includes(symbol) && (
          <span style={{ fontSize: 8, color: C.accent, display: 'block', marginTop: 6, lineHeight: 1.4 }}>
            ⚡ Index spot ka book exist nahi karta — ye chip ab us index ke LIVE FUTURES book par auto-land karta hai · option strike ke liye 🎯 OPTION DEPTH.
          </span>
        )}
      </div>
      {/* ── SMART OPTION PICKER — 1-2 taps → 20-level depth AUTO-subscribes ── */}
      <div style={{ padding: '6px 14px 8px', background: C.panelBg, borderBottom: `1px solid ${C.hairline}` }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
          <span style={{ fontSize: 8, color: '#a78bfa', fontWeight: 800, letterSpacing: 1 }}>🎯 OPTION DEPTH</span>
          <select value={pkUnd} onChange={e => { pkArmed.current = true; setPkUnd(e.target.value); }}
            style={{ background: 'rgba(255,255,255,.04)', border: `1px solid ${C.hairline}`, borderRadius: 5, color: '#fff', fontSize: 9.5, fontWeight: 700, padding: '3px 6px', outline: 'none' }}>
            {OPT_UNDERLYINGS.map(u => <option key={u} value={u}>{u.replace(/_/g, ' ')}</option>)}
          </select>
          <select value={pkExp} onChange={e => { pkArmed.current = true; setPkExp(e.target.value); }}
            style={{ background: 'rgba(255,255,255,.04)', border: `1px solid ${C.hairline}`, borderRadius: 5, color: '#fbbf24', fontSize: 9.5, fontWeight: 700, padding: '3px 6px', outline: 'none' }}>
            {pkExpiries.length === 0 && <option value="">loading…</option>}
            {pkExpiries.map((e2, i) => <option key={e2} value={e2}>{e2}{i === 0 ? ' · CUR' : ''}</option>)}
          </select>
          {(['CE', 'PE'] as const).map(s => (
            <button key={s} onClick={() => { pkArmed.current = true; setPkSide(s); }}
              style={{ padding: '3px 10px', borderRadius: 5, fontSize: 9.5, fontWeight: 900, cursor: 'pointer',
                background: pkSide === s ? (s === 'CE' ? 'rgba(16,185,129,.25)' : 'rgba(239,68,68,.25)') : 'rgba(255,255,255,.04)',
                border: `1px solid ${pkSide === s ? (s === 'CE' ? '#10b981' : '#ef4444') : C.hairline}`,
                color: pkSide === s ? (s === 'CE' ? '#10b981' : '#f87171') : C.muted }}>{s}</button>
          ))}
          <select value={pkStrike ?? ''} onChange={e => { pkArmed.current = true; setPkStrike(Number(e.target.value) || null); }}
            style={{ background: 'rgba(255,255,255,.04)', border: `1px solid ${C.hairline}`, borderRadius: 5, color: C.accentDim, fontSize: 9.5, fontWeight: 700, padding: '3px 6px', outline: 'none', maxWidth: 150 }}>
            <option value="">{pkLoading ? 'loading chain…' : 'All strikes…'}</option>
            {pkChain.map(r => (
              <option key={r.strike} value={r.strike}>
                {r.strike} · ₹{Number(pkSide === 'CE' ? r.ce_ltp : r.pe_ltp || 0).toFixed(1)}
              </option>
            ))}
          </select>
          <button onClick={useSignalOption}
            style={{ padding: '3px 9px', borderRadius: 5, fontSize: 9, fontWeight: 800, cursor: 'pointer',
              background: 'rgba(168,85,247,.15)', border: '1px solid #a855f7', color: '#c4b5fd' }}>
            ⚡ SIGNAL OPTION
          </button>
          <button onClick={() => setPkFollow(f => !f)}
            title="Auto-follow the active bot signal's option strike"
            style={{ padding: '3px 9px', borderRadius: 5, fontSize: 9, fontWeight: 800, cursor: 'pointer',
              background: pkFollow ? 'rgba(16,185,129,.22)' : 'rgba(255,255,255,.04)',
              border: `1px solid ${pkFollow ? '#10b981' : C.hairline}`,
              color: pkFollow ? '#34d399' : C.muted }}>
            🔗 BOTS{pkFollow ? ' ●' : ''}
          </button>
          {bookBias && (
            <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 5, fontSize: 9, fontWeight: 900,
              padding: '2px 8px', borderRadius: 5,
              background: bookBias.label.includes('BUY') ? 'rgba(16,185,129,.15)' : bookBias.label.includes('SELL') ? 'rgba(239,68,68,.15)' : 'rgba(148,163,184,.12)',
              border: `1px solid ${bookBias.label.includes('BUY') ? '#10b981' : bookBias.label.includes('SELL') ? '#ef4444' : '#334155'}`,
              color: bookBias.label.includes('BUY') ? '#34d399' : bookBias.label.includes('SELL') ? '#f87171' : C.label }}>
              📊 {bookBias.label} · {bookBias.buyPct}% buy
            </span>
          )}
        </div>
        {/* Smart strike row — ITM3…ATM…OTM3 from LIVE spot (shared chain) */}
        {pkAtmIdx >= 0 && (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
            {[-3, -2, -1, 0, 1, 2, 3].map(off => {
              const idx = pkSide === 'CE' ? pkAtmIdx + off : pkAtmIdx - off;
              const row = pkChain[idx];
              if (!row) return null;
              const lbl = off === 0 ? 'ATM' : (off < 0 ? `ITM${-off}` : `OTM${off}`);
              const ltp = Number(pkSide === 'CE' ? row.ce_ltp : row.pe_ltp || 0);
              const sel = pkStrike === Number(row.strike);
              return (
                <button key={off} onClick={() => setPkStrike(Number(row.strike))}
                  title={`${row.strike} ${pkSide} — LTP ₹${ltp.toFixed(2)}`}
                  style={{ padding: '3px 7px', borderRadius: 5, cursor: 'pointer', textAlign: 'center',
                    background: sel ? C.accentSoft : off === 0 ? 'rgba(251,191,36,.12)' : 'rgba(255,255,255,.04)',
                    border: `1px solid ${sel ? C.accent : off === 0 ? '#fbbf24' : C.hairline}`,
                    color: sel ? C.accent : off === 0 ? '#fbbf24' : C.label }}>
                  <div style={{ fontSize: 8, fontWeight: 900 }}>{lbl}</div>
                  <div style={{ fontSize: 8.5, fontFamily: 'monospace' }}>{row.strike}</div>
                  <div style={{ fontSize: 7.5, color: C.muted }}>₹{ltp.toFixed(1)}</div>
                </button>
              );
            })}
          </div>
        )}
        {pkMsg && <div style={{ fontSize: 8.5, marginTop: 4, color: pkMsg.startsWith('✓') ? '#10b981' : pkMsg.includes('invalid') || pkMsg.includes('failed') ? '#f87171' : C.label }}>{pkMsg}</div>}
      </div>
      {/* Options hint — free-type stays as fallback */}
      <div style={{ padding: '2px 14px 6px', background: C.subBg, borderBottom: `1px solid ${C.hairline}`, fontSize: 8.5, color: C.faint }}>
        💡 Fallback: type <span style={{ color: '#a78bfa', fontFamily: 'monospace' }}>NIFTY 23150 CE</span> in the box → SUB
      </div>

      {/* Symbol search box */}
      <div style={{ padding: '8px 14px', background: C.subBg, borderBottom: `1px solid ${C.hairline}`, display: 'flex', gap: 8, alignItems: 'center' }}>
        <input
          list="depth-sym-list"
          value={inputSym}
          onChange={e => setInputSym(e.target.value.toUpperCase())}
          onKeyDown={e => { if (e.key === 'Enter') onSymbolChange(inputSym); }}
          placeholder="Type index / stock / option  (Enter or SUB)"
          style={{ flex: 1, background: 'rgba(255,255,255,.04)', border: `1px solid ${C.hairline}`, borderRadius: 6, padding: '6px 10px', color: '#fff', fontSize: 11, outline: 'none' }}
        />
        <datalist id="depth-sym-list">
          {/* All indices */}
          {INDICES_LIST.map(s => <option key={`idx_${s}`} value={s} />)}
          {/* All 50 Nifty stocks */}
          {NIFTY_50_FNO_LIST.map(s => <option key={`stk_${s}`} value={s} />)}
          {/* Commodities & ETFs */}
          {COMMODITIES_LIST.map(s => <option key={`cmd_${s}`} value={s} />)}
          {ETFS.map(s => <option key={`etf_${s}`} value={s} />)}
        </datalist>
        <button
          onClick={() => onSymbolChange(inputSym)}
          style={{ padding: '5px 14px', background: C.accentSoft, border: `1px solid ${C.accent}`, color: C.accent, borderRadius: 6, cursor: 'pointer', fontSize: 10, fontWeight: 900, letterSpacing: 1 }}
        >SUB</button>
        {depth?.ltp != null && (
          <div style={{ fontSize: 13, fontWeight: 900, color: '#fbbf24', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
            ₹{depth.ltp.toFixed(2)}
          </div>
        )}
      </div>

      {wsError && (
        <div style={{ padding: '5px 14px', background: 'rgba(239,68,68,.1)', color: '#f87171', fontSize: 9, fontWeight: 700 }}>{wsError}</div>
      )}

      {marketClosed && (
        <div style={{ margin: '10px 14px', padding: '10px 16px', background: 'rgba(100,116,139,.1)', border: '1px solid #334155', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 18 }}>🔒</span>
          <div>
            <div style={{ fontSize: 11, fontWeight: 800, color: C.label }}>MARKET CLOSED</div>
            <div style={{ fontSize: 9, color: C.faint, marginTop: 2 }}>
              {/* The MCX close is SEASONAL — 23:55 in Nov-Mar on the US
                  daylight-saving linkage, 23:30 otherwise — and marketHours.ts
                  already knows that. Hardcoding "11:30 PM" made the caption
                  wrong for five months of the year, in the one panel a trader
                  reads to decide whether the tape should be moving. */}
              NSE/BSE: 9:15 AM – 3:40 PM &nbsp;|&nbsp; MCX: 9:00 AM – {mcxCloseLabel()}
            </div>
            {depth && (
              <div style={{ fontSize: 9, color: '#fbbf24', marginTop: 3, fontFamily: 'monospace' }}>
                last snapshot {snapClock(depth.timestamp)} (market closed)
              </div>
            )}
          </div>
        </div>
      )}

      {/* Order Book Table — 20-Level: Orders | Qty | BID PRICE | # | ASK PRICE | Qty | Orders */}
      <div style={{ padding: '0 10px 12px' }}>
        {/* ── PRO STATS STRIP (God 2026-07-27 redesign): LTP · spread · book bias ── */}
        {depth && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '8px 6px', borderBottom: `1px solid ${C.hairline}` }}>
            {depth.ltp != null && (
              <span style={{ fontSize: 17, fontWeight: 900, color: '#fbbf24', fontFamily: 'monospace' }}>₹{depth.ltp.toFixed(2)}</span>
            )}
            <span style={{ fontSize: 9, fontWeight: 800, padding: '3px 9px', borderRadius: 99, background: 'rgba(251,191,36,.1)', border: '1px solid rgba(251,191,36,.35)', color: '#fbbf24', fontFamily: 'monospace' }}>
              SPREAD ₹{spread.toFixed(2)}
            </span>
            {bookBias && (
              <span style={{ fontSize: 9, fontWeight: 900, padding: '3px 9px', borderRadius: 99,
                background: bookBias.buyPct >= 56 ? 'rgba(16,185,129,.14)' : bookBias.buyPct <= 44 ? 'rgba(239,68,68,.12)' : 'rgba(148,163,184,.1)',
                border: `1px solid ${bookBias.buyPct >= 56 ? 'rgba(16,185,129,.5)' : bookBias.buyPct <= 44 ? 'rgba(239,68,68,.5)' : '#334155'}`,
                color: bookBias.buyPct >= 56 ? '#10b981' : bookBias.buyPct <= 44 ? '#ef4444' : C.label }}>
                {bookBias.label} · {bookBias.buyPct}% BUY
              </span>
            )}
            <span style={{ fontSize: 8.5, fontWeight: 800, padding: '3px 8px', borderRadius: 99,
              background: isFullDepth ? 'rgba(16,185,129,.12)' : 'rgba(148,163,184,.12)',
              border: `1px solid ${isFullDepth ? 'rgba(16,185,129,.45)' : 'rgba(148,163,184,.3)'}`,
              color: isFullDepth ? '#34d399' : C.label }}>
              {bookLevels}L · {isFullDepth ? 'FULL DEPTH' : 'EXCHANGE MAX'}
            </span>
            <span style={{ marginLeft: 'auto', fontSize: 8, color: C.faint }}>walls = qty ≥ 2.5× avg (glow) · bars = cumulative depth</span>
          </div>
        )}

        {/* Pre-open auction / stale mix — the server has flagged this since the
            panel was written and nothing ever read it. A crossed book (bid ≥ ask)
            is not a tradable book, and silently drawing it as one is how a
            pre-open print gets mistaken for a real spread. */}
        {depth?.crossed && (
          <div style={{ margin: '6px 6px 0', padding: '6px 10px', borderRadius: 6,
            background: 'rgba(251,191,36,.10)', border: '1px solid rgba(251,191,36,.4)',
            fontSize: 9, fontWeight: 700, color: '#fbbf24' }}>
            ⚠️ CROSSED BOOK — best bid ≥ best ask. Pre-open auction or a stale frame mix; do not read this spread as tradable.
          </div>
        )}

        {/* A 5-level book is the exchange ceiling on MCX, not a broken 20-level
            one. Saying so here stops the next "depth panel is half-empty" hunt. */}
        {depth && !isFullDepth && (
          <div style={{ margin: '6px 6px 0', padding: '5px 10px', borderRadius: 6,
            background: C.accentFaint, border: `1px solid ${C.hairline}`,
            fontSize: 8.5, color: C.label, lineHeight: 1.45 }}>
            ℹ️ {bookLevels}-level book — Dhan enables Full (20-level) Depth for <b>NSE equity &amp; derivatives only</b>.
            MCX comes back as a {bookLevels}-level REST quote; this is the deepest the exchange offers here, not a feed fault.
          </div>
        )}
        {/* Column headers */}
        <div style={{ display: 'grid', gridTemplateColumns: '44px 52px 1fr 22px 1fr 52px 44px', borderBottom: `1px solid ${C.hairline}` }}>
          <span style={{ ...colHeader, color: '#10b981' }}>ORDERS</span>
          <span style={{ ...colHeader, textAlign: 'right', color: '#10b981' }}>QTY</span>
          <span style={{ ...colHeader, textAlign: 'right', color: '#10b981' }}>BID PRICE</span>
          <span style={{ ...colHeader, textAlign: 'center' }}>#</span>
          <span style={{ ...colHeader, color: '#ef4444' }}>ASK PRICE</span>
          <span style={{ ...colHeader, textAlign: 'right', color: '#ef4444' }}>QTY</span>
          <span style={{ ...colHeader, textAlign: 'right', color: '#ef4444' }}>ORDERS</span>
        </div>

        {!depth ? (
          <div style={{ textAlign: 'center', padding: '28px 0', color: C.faint, fontSize: 11 }}>
            {connected ? `Subscribed to ${symbol} — waiting for data…` : 'Connecting to Dhan HQ…'}
            {connected && waitNote && (
              <div style={{ marginTop: 8, color: '#f59e0b', fontSize: 10, lineHeight: 1.5 }}>💡 {waitNote}</div>
            )}
          </div>
        ) : (
          <>
            <div className="lt-scroll" style={{ maxHeight: 360, overflowY: 'auto' }}>
              {/* rowCount, not 20 — see the depth-honesty note at the top of the file */}
              {Array.from({ length: rowCount }).map((_, i) => {
                const bid = depth.bids[i];
                const ask = depth.asks[i];
                const isTop = i < 5;
                return (
                  <div key={i} style={{
                    display: 'grid', gridTemplateColumns: '44px 52px 1fr 22px 1fr 52px 44px',
                    borderBottom: `1px solid ${isTop ? '#131c2f' : '#0c1220'}`,
                    position: 'relative', alignItems: 'center', minHeight: 22,
                    background: isTop ? (i % 2 === 0 ? 'rgba(16,185,129,.03)' : 'transparent') : 'transparent',
                  }}>
                    {/* Cumulative depth (light wash) + this-level bar (solid) — pro DOM look */}
                    {bid && <div style={{ position: 'absolute', right: '50%', top: 0, bottom: 0, width: `${(cumBids[i] / (totalBidQty || 1)) * 46}%`, background: 'rgba(16,185,129,.05)' }} />}
                    {bid && <div style={{ position: 'absolute', right: '50%', top: 0, bottom: 0, width: `${(bid.qty / maxBidQty) * 46}%`, background: bid.qty >= avgBidQty * 2.5 ? 'rgba(16,185,129,.28)' : 'rgba(16,185,129,.12)', boxShadow: bid.qty >= avgBidQty * 2.5 ? 'inset 0 0 8px rgba(16,185,129,.5)' : 'none' }} />}
                    {ask && <div style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: `${(cumAsks[i] / (totalAskQty || 1)) * 46}%`, background: 'rgba(239,68,68,.045)' }} />}
                    {ask && <div style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: `${(ask.qty / maxAskQty) * 46}%`, background: ask.qty >= avgAskQty * 2.5 ? 'rgba(239,68,68,.25)' : 'rgba(239,68,68,.10)', boxShadow: ask.qty >= avgAskQty * 2.5 ? 'inset 0 0 8px rgba(239,68,68,.5)' : 'none' }} />}

                    {/* Bid Orders */}
                    {bid?.orders != null ? (
                      <span style={{ fontSize: 8, color: '#10b98188', padding: '0 4px', zIndex: 1 }}>{bid.orders}</span>
                    ) : <span />}
                    {/* Bid Qty */}
                    {bid ? (
                      <span style={{ textAlign: 'right', color: isTop ? '#10b981' : '#059669', fontFamily: 'monospace', fontSize: isTop ? 10 : 9, fontWeight: isTop ? 800 : 600, padding: '0 4px', zIndex: 1 }}>
                        {bid.qty.toLocaleString('en-IN')}
                      </span>
                    ) : <span />}
                    {/* Bid Price */}
                    {bid ? (
                      <span style={{ textAlign: 'right', color: isTop ? '#10b981' : '#34d399', fontFamily: 'monospace', fontSize: isTop ? 11 : 10, fontWeight: isTop ? 900 : 700, padding: '0 6px', zIndex: 1 }}>
                        {bid.price.toFixed(2)}
                      </span>
                    ) : <span />}

                    {/* Row number */}
                    <span style={{ fontSize: 7, color: isTop ? C.faint : C.hairline, textAlign: 'center', zIndex: 1 }}>{i + 1}</span>

                    {/* Ask Price */}
                    {ask ? (
                      <span style={{ textAlign: 'left', color: isTop ? '#ef4444' : '#f87171', fontFamily: 'monospace', fontSize: isTop ? 11 : 10, fontWeight: isTop ? 900 : 700, padding: '0 6px', zIndex: 1 }}>
                        {ask.price.toFixed(2)}
                      </span>
                    ) : <span />}
                    {/* Ask Qty */}
                    {ask ? (
                      <span style={{ textAlign: 'right', color: isTop ? '#ef4444' : '#dc2626', fontFamily: 'monospace', fontSize: isTop ? 10 : 9, fontWeight: isTop ? 800 : 600, padding: '0 4px', zIndex: 1 }}>
                        {ask.qty.toLocaleString('en-IN')}
                      </span>
                    ) : <span />}
                    {/* Ask Orders */}
                    {ask?.orders != null ? (
                      <span style={{ textAlign: 'right', fontSize: 8, color: '#ef444488', padding: '0 4px', zIndex: 1 }}>{ask.orders}</span>
                    ) : <span />}
                  </div>
                );
              })}
            </div>

            {/* Totals */}
            <div style={{ display: 'grid', gridTemplateColumns: '44px 52px 1fr 22px 1fr 52px 44px', padding: '4px 0', borderTop: `1px solid ${C.hairline}` }}>
              <span />
              <span style={{ textAlign: 'right', color: '#10b981', fontSize: 9, fontWeight: 900, padding: '0 4px' }}>
                {totalBidQty.toLocaleString('en-IN')}
              </span>
              <span style={{ textAlign: 'right', color: C.muted, fontSize: 8, padding: '0 6px' }}>TOTAL</span>
              <span />
              <span style={{ color: C.muted, fontSize: 8, padding: '0 6px' }}>TOTAL</span>
              <span style={{ textAlign: 'right', color: '#ef4444', fontSize: 9, fontWeight: 900, padding: '0 4px' }}>
                {totalAskQty.toLocaleString('en-IN')}
              </span>
              <span />
            </div>

            {/* Buyer/Seller strength bar */}
            <div style={{ margin: '8px 8px 0', borderRadius: 4, overflow: 'hidden', height: 6, display: 'flex' }}>
              {(() => {
                const total = totalBidQty + totalAskQty || 1;
                const bidPct = (totalBidQty / total) * 100;
                return (
                  <>
                    <div style={{ width: `${bidPct}%`, background: 'linear-gradient(90deg,#10b981,#059669)', transition: 'width .4s' }} />
                    <div style={{ flex: 1, background: 'linear-gradient(90deg,#dc2626,#ef4444)' }} />
                  </>
                );
              })()}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 8px', fontSize: 8, color: C.muted }}>
              <span style={{ color: '#10b981', fontWeight: 700 }}>
                BUYERS {((totalBidQty / (totalBidQty + totalAskQty || 1)) * 100).toFixed(1)}%
              </span>
              <span style={{ color: '#ef4444', fontWeight: 700 }}>
                {((totalAskQty / (totalBidQty + totalAskQty || 1)) * 100).toFixed(1)}% SELLERS
              </span>
            </div>
          </>
        )}
      </div>

      {depth?.timestamp && (
        <div style={{ padding: '6px 14px', background: C.footBg, borderTop: `1px solid ${C.hairline}`, fontSize: 8, color: C.faint, display: 'flex', justifyContent: 'space-between' }}>
          <span>{srcLabel || 'Dhan HQ Level-2'} · {bookLevels}-level book</span>
          <span>{depth.timestamp}</span>
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
//  SYMBOL CONTROL STRIP — L0 symbol-kill chips (2026-07-09)
//  Blueprint FUND_MANAGEMENT_BLUEPRINT.md §5: the daily surgical kill
//  lives HERE next to the global KILL ALL (L3). One chip per active
//  symbol; kill blocks NEW entries for that symbol across ALL bots
//  (exits/square-offs are never blocked). Same endpoints as the
//  FUNDS HUB panel: /risk/symbol-controls, /risk/symbol-kill(/reset).
// ══════════════════════════════════════════════════════════════
